import React from 'react';
import { motion } from 'framer-motion';
import { DeckLogoPlate } from './DeckLogo';
const EASE = [0.23, 1, 0.32, 1] as const;
interface SlideFrameProps {
  eyebrow: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  tone?: 'light' | 'dark';
  /** Rendered flush under the title, filling the remaining stage height. */
  children?: React.ReactNode;
  footnote?: React.ReactNode;
  /** Narrower title column so a visual can sit beside it. */
  compactHeader?: boolean;
}
export function SlideFrame({
  eyebrow,
  title,
  lead,
  tone = 'light',
  children,
  footnote,
  compactHeader = false
}: SlideFrameProps) {
  const dark = tone === 'dark';
  return <div className={`relative w-full h-full overflow-hidden ${dark ? 'bg-dark-navy' : 'bg-white'}`} data-id="element-1786" data-mp="29xdky67zp035">
      <DeckAmbience tone={tone} data-id="element-1787" data-mp="ygduzh946xca" />

      <div className="relative z-10 flex flex-col h-full px-14 pt-10 pb-9" data-id="element-1788" data-mp="yhgj5hf1yloi">
        <motion.div className="flex items-center gap-3 shrink-0" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.45,
        ease: EASE
      }} data-id="element-1789" data-mp="thkhhottv6c4">
          <DeckLogoPlate tone={dark ? 'dark' : 'light'} data-id="element-1790" data-mp="1jit1tpcridmg" />
          <span className={`text-[10px] font-semibold uppercase tracking-[0.22em] ${dark ? 'text-white/45' : 'text-slate-400'}`} data-id="element-1791" data-mp="14mlkqxwnhsbe">
            {eyebrow}
          </span>
        </motion.div>

        <div className={`shrink-0 mt-5 ${compactHeader ? 'max-w-[540px]' : 'max-w-[880px]'}`} data-id="element-1792" data-mp="vt8hb20yn8b2">
          <motion.h2 className={`font-display font-bold tracking-tight ${dark ? 'text-white' : 'text-dark-navy'} ${compactHeader ? 'text-[34px] leading-[1.1]' : 'text-[40px] leading-[1.08]'}`} initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          ease: EASE,
          delay: 0.06
        }} data-id="element-1793" data-mp="10m1n7t860moq">
            {title}
          </motion.h2>
          {lead && <motion.p className={`mt-3 text-[13.5px] leading-relaxed ${dark ? 'text-white/60' : 'text-slate-500'}`} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: EASE,
          delay: 0.16
        }} data-id="element-1794" data-mp="2jjo7zru8n8cx">
              {lead}
            </motion.p>}
        </div>

        {children && <div className="relative flex-1 min-h-0 mt-7" data-id="element-1795" data-mp="32j20e5cbkb1b">{children}</div>}

        {footnote && <motion.div className="shrink-0 mt-6" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: EASE,
        delay: 0.5
      }} data-id="element-1796" data-mp="2xixnepamdyfu">
            {footnote}
          </motion.div>}
      </div>
    </div>;
}

/** Shared background: quiet dotted texture plus one slow-drifting brand glow. */
export function DeckAmbience({
  tone = 'light'
}: {
  tone?: 'light' | 'dark';
}) {
  const dark = tone === 'dark';
  return <div className="absolute inset-0 overflow-hidden" aria-hidden="true" data-id="element-1797" data-mp="3orqryq6aj6ug">
      <div className="absolute inset-0" style={{
      backgroundImage: `radial-gradient(circle at center, ${dark ? 'rgba(255,255,255,0.07)' : 'rgba(67,56,202,0.10)'} 1px, transparent 1px)`,
      backgroundSize: '26px 26px',
      opacity: dark ? 0.7 : 0.55
    }} data-id="element-1798" data-mp="3ss9j0i0d08vl" />
      <motion.div className="absolute -top-40 -right-24 w-[620px] h-[620px] rounded-full" style={{
      background: dark ? 'radial-gradient(circle, rgba(168,85,247,0.28), transparent 68%)' : 'radial-gradient(circle, rgba(168,85,247,0.13), transparent 68%)',
      filter: 'blur(10px)'
    }} animate={{
      x: [0, -26, 0],
      y: [0, 20, 0]
    }} transition={{
      duration: 18,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-1799" data-mp="bazug22or7dh" />
      <motion.div className="absolute -bottom-52 -left-32 w-[640px] h-[640px] rounded-full" style={{
      background: dark ? 'radial-gradient(circle, rgba(67,56,202,0.35), transparent 70%)' : 'radial-gradient(circle, rgba(34,211,238,0.10), transparent 70%)',
      filter: 'blur(10px)'
    }} animate={{
      x: [0, 24, 0],
      y: [0, -18, 0]
    }} transition={{
      duration: 22,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-1800" data-mp="2r2nnvnihqhrj" />
      {!dark && <div className="absolute inset-0" style={{
      background: 'radial-gradient(ellipse at 50% 45%, rgba(255,255,255,0.92) 32%, rgba(255,255,255,0.55) 62%, rgba(255,255,255,0) 100%)'
    }} data-id="element-1801" data-mp="1d1zpidd4likn" />}
    </div>;
}

/** Small metric chip used to contrast effort before/after Talab. */
export function MetricChip({
  label,
  value,
  tone = 'neutral'
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'warning' | 'positive';
}) {
  const styles = {
    neutral: 'bg-white border-slate-200 text-dark-navy',
    warning: 'bg-error/[0.06] border-error/25 text-error',
    positive: 'bg-success/[0.07] border-success/25 text-success'
  }[tone];
  return <div className={`inline-flex items-baseline gap-2 px-3.5 py-2 rounded-xl border ${styles}`} data-id="element-1802" data-mp="35s1xpot9ll14">
      <span className="font-display font-bold text-[15px] leading-none" data-id="element-1803" data-mp="2r35hv5kyv3py">{value}</span>
      <span className="text-[10.5px] uppercase tracking-[0.14em] opacity-70" data-id="element-1804" data-mp="2w0fhtf5zexs1">{label}</span>
    </div>;
}